import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  api, MatchExtra, FormGame, TeamRank, Weather, ModelWeights, FactorKey, DEFAULT_WEIGHTS,
} from '../api/client';
import { useStore } from '../stores/store';
import { Flag } from './TeamBadge';
import { weatherInfo } from '../lib/weather';
import { Venue } from '../lib/venues';
import { H2 } from './ui';

type TeamRef = { name: string; flag: string };

const clamp = (x: number, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, x));

// Cada factor devuelve la "expectativa" de team1 (0..1; >0.5 favorece a team1) + su peso.
interface Factor {
  key: FactorKey;
  label: string;
  e1: number;
  weight: number;
  note: string; // a quién favorece, en breve
}

// Previa / pronosticador multi-factor: combina ranking FIFA (Elo), forma reciente (con peso a lo
// más nuevo), goles a favor/en contra, clima de la sede vs adaptación de cada selección, y el
// mano a mano. Cada factor con distinto peso → favoritismo, probabilidad 1/X/2 y desglose visible.
export function MatchPreview({
  data,
  t1,
  t2,
  venue,
  compact = false,
}: {
  data: MatchExtra;
  t1: TeamRef;
  t2: TeamRef;
  venue: Venue | null;
  compact?: boolean; // en el prode: solo el resumen (barra de favoritismo + veredicto)
}) {
  const f1 = data.form?.team1 ?? [];
  const f2 = data.form?.team2 ?? [];
  const h2h = data.h2h ?? null;
  const r1 = data.ranks?.team1 ?? null;
  const r2 = data.ranks?.team2 ?? null;

  const [weather, setWeather] = useState<Weather | null>(null);
  useEffect(() => {
    if (!venue) return;
    api.weather(venue.lat, venue.lon).then(setWeather).catch(() => {});
  }, [venue?.ground, venue]);
  const venueTemp = weather?.ok && weather.temp != null ? weather.temp : null;

  // Pesos efectivos: personalizados del usuario > calibrados (auto) > por defecto.
  const predictAuto = useStore((s) => s.predictAuto);
  const predictWeights = useStore((s) => s.predictWeights);
  const setOracle = useStore((s) => s.setOracleTuning);
  const oracleOpen = useStore((s) => s.oracleTuningOpen);

  // Al cerrar el ajuste, "el Oráculo medita" ~1.3s y después revela el pronóstico recalculado.
  const [thinking, setThinking] = useState(false);
  const prevOpen = useRef(oracleOpen);
  useEffect(() => {
    if (prevOpen.current && !oracleOpen) {
      setThinking(true);
      const id = setTimeout(() => setThinking(false), 1300);
      prevOpen.current = oracleOpen;
      return () => clearTimeout(id);
    }
    prevOpen.current = oracleOpen;
  }, [oracleOpen]);
  const [auto, setAuto] = useState<ModelWeights | null>(null);
  useEffect(() => {
    if (predictAuto && !predictWeights) {
      api.modelTuning().then((t) => setAuto(t.weights)).catch(() => {});
    }
  }, [predictAuto, predictWeights]);
  const weights: ModelWeights = predictWeights ?? (predictAuto ? auto ?? DEFAULT_WEIGHTS : DEFAULT_WEIGHTS);

  if (f1.length === 0 && f2.length === 0 && !h2h && !r1 && !r2) return null;

  const factors = buildFactors(t1.name, t2.name, r1, r2, f1, f2, h2h, venueTemp, weights);
  const wsum = factors.reduce((s, f) => s + f.weight, 0) || 1;
  const e1 = factors.reduce((s, f) => s + f.e1 * f.weight, 0) / wsum;
  const pct1 = Math.round(e1 * 100);
  const pct2 = 100 - pct1;
  const gap = Math.abs(pct1 - 50);
  const fav = pct1 >= pct2 ? t1.name : t2.name;
  const verdict =
    gap < 4 ? 'Parejo' : gap < 12 ? `Leve favorito: ${fav}` : gap < 23 ? `Favorito: ${fav}` : `Gran favorito: ${fav}`;
  const prob = win123(e1);

  // En el prode mostramos solo el resumen: la barra de favoritismo (porcentajes) + el veredicto.
  if (compact) {
    return (
      <Card>
        <div className="hd"><span className="ico">🔮</span> El Oráculo</div>
        <Meter>
          <Flag team={t1.flag} size={34} />
          <div className="bar">
            <div className="a" style={{ flex: Math.max(pct1, 1) }} />
            <div className="b" style={{ flex: Math.max(pct2, 1) }} />
            <span className="p1">{pct1}%</span>
            <span className="p2">{pct2}%</span>
          </div>
          <Flag team={t2.flag} size={34} />
        </Meter>
        <div className="verdict"><strong>{verdict}</strong></div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="hd">
        <span className="ico">🔮</span> El Oráculo
        <button className="tune" onClick={() => setOracle(true)}>⚙️ Ajustar</button>
      </div>

      {thinking ? (
        <Thinking>
          <div className="orb">🔮</div>
          <div className="ring" />
          <div className="txt">El Oráculo medita<span className="dots" /></div>
        </Thinking>
      ) : (
      <>
      <Meter>
        <Flag team={t1.flag} size={34} />
        <div className="bar">
          <div className="a" style={{ flex: Math.max(pct1, 1) }} />
          <div className="b" style={{ flex: Math.max(pct2, 1) }} />
          <span className="p1">{pct1}%</span>
          <span className="p2">{pct2}%</span>
        </div>
        <Flag team={t2.flag} size={34} />
      </Meter>
      <div className="verdict">
        <strong>{verdict}</strong>
      </div>

      <H2>Probabilidad</H2>
      <Prob>
        <div className="bar">
          <div className="p1" style={{ flex: Math.max(prob.p1, 1) }}>{prob.p1}%</div>
          <div className="pX" style={{ flex: Math.max(prob.pX, 1) }}>{prob.pX}%</div>
          <div className="p2" style={{ flex: Math.max(prob.p2, 1) }}>{prob.p2}%</div>
        </div>
        <div className="lg">
          <span><i className="c1" /> 1 · {t1.name}</span>
          <span><i className="cX" /> X · empate</span>
          <span><i className="c2" /> 2 · {t2.name}</span>
        </div>
      </Prob>

      <H2>Factores</H2>
      <Factors>
        {factors.map((f) => (
          <div className="f" key={f.label}>
            <div className="top">
              <span className="lb">{f.label}</span>
              <span className="w">{Math.round((f.weight / wsum) * 100)}%</span>
            </div>
            <div className="bar">
              <div className="a" style={{ flex: Math.max(Math.round(f.e1 * 100), 1) }} />
              <div className="b" style={{ flex: Math.max(100 - Math.round(f.e1 * 100), 1) }} />
            </div>
            <div className="note">{f.note}</div>
          </div>
        ))}
      </Factors>

      <H2>Forma reciente</H2>
      <FormRow name={t1.name} games={f1} />
      <FormRow name={t2.name} games={f2} />

      {h2h && h2h.total > 0 && (
        <>
          <H2>Mano a mano</H2>
          <H2h>
            <div className="tot">
              <span className="w">{h2h.team1Wins}</span>
              <span className="lbl">{t1.name}</span>
              <span className="d">{h2h.draws} E</span>
              <span className="lbl">{t2.name}</span>
              <span className="l">{h2h.team2Wins}</span>
            </div>
            <div className="games">
              {h2h.games.slice(0, 5).map((g, i) => (
                <div className="g" key={i}>
                  <span className="dt">{g.date}</span>
                  <span className="sc">{g.score}</span>
                  <span className="cp">{g.comp}</span>
                </div>
              ))}
            </div>
          </H2h>
        </>
      )}

      <div className="disc">
        Modelo orientativo · ajustá los pesos con ⚙️ Ajustar.
        {venueTemp != null && weather?.code != null
          ? ` Clima en la sede: ${weatherInfo(weather.code).emoji} ${venueTemp}°.`
          : ''}
      </div>
      </>
      )}
    </Card>
  );
}

function buildFactors(
  n1: string,
  n2: string,
  r1: TeamRank | null,
  r2: TeamRank | null,
  f1: FormGame[],
  f2: FormGame[],
  h2h: MatchExtra['h2h'],
  venueTemp: number | null,
  weights: ModelWeights,
): Factor[] {
  const out: Factor[] = [];

  // Ranking FIFA — estilo Elo sobre la diferencia de puntos (amplifica brechas reales).
  if (r1 && r2) {
    const e = 1 / (1 + Math.pow(10, -(r1.points - r2.points) / 500));
    out.push({ key: 'ranking', label: 'Ranking FIFA', e1: e, weight: weights.ranking, note: favNote(e, n1, n2, `#${r1.rank} vs #${r2.rank}`) });
  }

  // Forma reciente — puntos por partido con MÁS peso a los más recientes.
  if (f1.length > 0 || f2.length > 0) {
    const w1 = weightedPPG(f1);
    const w2 = weightedPPG(f2);
    const e = clamp(0.5 + (w1 - w2) / 6);
    out.push({ key: 'form', label: 'Forma reciente', e1: e, weight: weights.form, note: favNote(e, n1, n2, `${w1.toFixed(1)} vs ${w2.toFixed(1)} pts/pj`) });
  }

  // Goles — diferencia de gol promedio reciente (ataque vs defensa).
  if (f1.length > 0 || f2.length > 0) {
    const gd1 = avg(f1, 'gf') - avg(f1, 'ga');
    const gd2 = avg(f2, 'gf') - avg(f2, 'ga');
    const e = clamp(0.5 + (gd1 - gd2) / 6);
    out.push({ key: 'goals', label: 'Goles (atq/def)', e1: e, weight: weights.goals, note: favNote(e, n1, n2, `${signed(gd1)} vs ${signed(gd2)}`) });
  }

  // Clima — qué selección está más adaptada a la temperatura de la sede.
  if (venueTemp != null && r1?.climate != null && r2?.climate != null) {
    const a1 = Math.abs(venueTemp - r1.climate);
    const a2 = Math.abs(venueTemp - r2.climate);
    const e = clamp(0.5 + (a2 - a1) / 24);
    out.push({ key: 'climate', label: `Clima (${venueTemp}°)`, e1: e, weight: weights.climate, note: favNote(e, n1, n2, `${r1.climate}° vs ${r2.climate}° hab.`) });
  }

  // Mano a mano.
  if (h2h && h2h.total > 0) {
    const e = (h2h.team1Wins + 0.5 * h2h.draws) / h2h.total;
    out.push({ key: 'h2h', label: 'Mano a mano', e1: e, weight: weights.h2h, note: favNote(e, n1, n2, `${h2h.team1Wins}-${h2h.draws}-${h2h.team2Wins}`) });
  }

  return out.filter((f) => f.weight > 0);
}

function favNote(e: number, n1: string, n2: string, detail: string): string {
  const who = Math.abs(e - 0.5) < 0.03 ? 'parejo' : e > 0.5 ? `→ ${n1}` : `→ ${n2}`;
  return `${detail}  ${who}`;
}

function weightedPPG(games: FormGame[]): number {
  if (games.length === 0) return 0;
  let num = 0;
  let den = 0;
  games.forEach((g, i) => {
    const w = games.length - i; // el primero (más reciente) pesa más
    const pts = g.result === 'W' ? 3 : g.result === 'D' ? 1 : 0;
    num += pts * w;
    den += w;
  });
  return den > 0 ? num / den : 0;
}

function avg(games: FormGame[], key: 'gf' | 'ga'): number {
  if (games.length === 0) return 0;
  return games.reduce((s, g) => s + (g[key] ?? 0), 0) / games.length;
}

const signed = (x: number) => (x >= 0 ? '+' : '') + x.toFixed(1);

// Probabilidad 1/X/2 desde la expectativa e1. El empate sube cuanto más pareja es la expectativa.
function win123(e1: number): { p1: number; pX: number; p2: number } {
  const pDraw = clamp(0.3 * (1 - 2 * Math.abs(e1 - 0.5)), 0.1, 0.32);
  const rem = 1 - pDraw;
  const p1 = Math.round(rem * e1 * 100);
  const p2 = Math.round(rem * (1 - e1) * 100);
  return { p1, p2, pX: 100 - p1 - p2 };
}

function FormRow({ name, games }: { name: string; games: FormGame[] }) {
  if (games.length === 0) return null;
  return (
    <Form>
      <div className="top">
        <span className="nm">{name}</span>
        <span className="dots">
          {games.map((g, i) => (
            <span key={i} className={`d ${g.result}`} title={`${g.opp} ${g.score}`} />
          ))}
        </span>
      </div>
      <div className="goals">
        ⚽ {avg(games, 'gf').toFixed(1)} GF · {avg(games, 'ga').toFixed(1)} GC{' '}
        <span className="muted">(prom. últimos {games.length})</span>
      </div>
    </Form>
  );
}

const Card = styled.div`
  background: linear-gradient(160deg, ${({ theme }) => theme.colors.accentDark}, ${({ theme }) => theme.colors.surface} 70%);
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius};
  padding: 14px;
  margin: 8px 0 4px;
  color: ${({ theme }) => theme.colors.text};

  .hd { font-weight: 800; font-size: 1.15rem; margin-bottom: 12px; display: flex; align-items: center; gap: 7px; }
  .hd .tune {
    margin-left: auto;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text};
    border-radius: 999px;
    padding: 5px 12px;
    font-size: 0.75rem;
    font-weight: 700;
  }
  .verdict { display: flex; justify-content: center; margin-top: 8px; }
  .verdict strong { font-size: 1.1rem; }
  .disc { margin-top: 12px; font-size: 0.72rem; text-align: center; color: ${({ theme }) => theme.colors.textMuted}; }
`;

const Thinking = styled.div`
  position: relative;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;

  .orb {
    font-size: 4.6rem;
    line-height: 1;
    filter: drop-shadow(0 0 18px rgba(124, 58, 237, 0.8));
    animation: orbFloat 2s ease-in-out infinite;
    z-index: 1;
  }
  /* Halo místico girando detrás de la bola. */
  .ring {
    position: absolute;
    top: calc(50% - 70px);
    width: 130px;
    height: 130px;
    border-radius: 50%;
    background: conic-gradient(from 0deg, transparent, ${({ theme }) => theme.colors.accent}, #7c3aed, transparent);
    opacity: 0.55;
    filter: blur(6px);
    animation: ringSpin 1.4s linear infinite;
  }
  .txt {
    z-index: 1;
    font-weight: 800;
    font-size: 1.05rem;
    letter-spacing: 0.5px;
  }
  .dots::after {
    content: '';
    animation: dots 1.2s steps(4, end) infinite;
  }
  @keyframes orbFloat {
    0%, 100% { transform: translateY(0) rotate(-4deg); }
    50% { transform: translateY(-8px) rotate(4deg); }
  }
  @keyframes ringSpin {
    to { transform: rotate(360deg); }
  }
  @keyframes dots {
    0% { content: ''; }
    25% { content: '.'; }
    50% { content: '..'; }
    75% { content: '...'; }
  }
`;

const Meter = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  .bar {
    position: relative;
    flex: 1;
    display: flex;
    height: 26px;
    border-radius: 999px;
    overflow: hidden;
    background: ${({ theme }) => theme.colors.surfaceAlt};
    font-size: 0.75rem;
    font-weight: 800;
  }
  .bar .a { background: ${({ theme }) => theme.colors.accent}; }
  .bar .b { background: ${({ theme }) => theme.colors.gold}; }
  .bar .p1 { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #fff; }
  .bar .p2 { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); color: #fff; }
`;

const Prob = styled.div`
  .bar { display: flex; height: 30px; border-radius: 8px; overflow: hidden; font-size: 0.79rem; font-weight: 800; color: #fff; }
  .bar > div { display: flex; align-items: center; justify-content: center; min-width: 0; }
  .bar .p1 { background: ${({ theme }) => theme.colors.accent}; }
  .bar .pX { background: ${({ theme }) => theme.colors.textMuted}; }
  .bar .p2 { background: ${({ theme }) => theme.colors.gold}; }
  .lg { display: flex; flex-wrap: wrap; gap: 4px 14px; justify-content: center; margin-top: 8px; font-size: 0.75rem; color: ${({ theme }) => theme.colors.textMuted}; }
  .lg span { display: inline-flex; align-items: center; gap: 5px; }
  .lg i { width: 10px; height: 10px; border-radius: 2px; display: inline-block; }
  .lg .c1 { background: ${({ theme }) => theme.colors.accent}; }
  .lg .cX { background: ${({ theme }) => theme.colors.textMuted}; }
  .lg .c2 { background: ${({ theme }) => theme.colors.gold}; }
`;

const Factors = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  .f .top { display: flex; justify-content: space-between; font-size: 0.81rem; }
  .f .top .lb { font-weight: 700; }
  .f .top .w { color: ${({ theme }) => theme.colors.textMuted}; font-weight: 700; font-size: 0.71rem; }
  .f .bar { display: flex; height: 6px; border-radius: 999px; overflow: hidden; margin: 3px 0; background: ${({ theme }) => theme.colors.surfaceAlt}; }
  .f .bar .a { background: ${({ theme }) => theme.colors.accent}; }
  .f .bar .b { background: ${({ theme }) => theme.colors.gold}; }
  .f .note { font-size: 0.71rem; color: ${({ theme }) => theme.colors.textMuted}; }
`;

const Form = styled.div`
  margin-bottom: 10px;
  .top { display: flex; align-items: center; gap: 10px; }
  .nm { flex: 1; font-weight: 700; font-size: 0.9rem; }
  .dots { display: flex; gap: 5px; }
  .dots .d { width: 16px; height: 16px; border-radius: 50%; display: inline-block; }
  .dots .W { background: ${({ theme }) => theme.colors.accent}; }
  .dots .D { background: ${({ theme }) => theme.colors.textMuted}; }
  .dots .L { background: ${({ theme }) => theme.colors.live}; }
  .goals { font-size: 0.79rem; color: ${({ theme }) => theme.colors.text}; margin-top: 2px; }
  .goals .muted { color: ${({ theme }) => theme.colors.textMuted}; }
`;

const H2h = styled.div`
  .tot { display: flex; align-items: center; justify-content: center; gap: 8px; font-size: 0.86rem; margin-bottom: 8px; }
  .tot .w { font-weight: 900; font-size: 1.13rem; color: ${({ theme }) => theme.colors.accent}; }
  .tot .l { font-weight: 900; font-size: 1.13rem; color: ${({ theme }) => theme.colors.gold}; }
  .tot .d { font-weight: 700; color: ${({ theme }) => theme.colors.textMuted}; }
  .tot .lbl { font-size: 0.77rem; color: ${({ theme }) => theme.colors.textMuted}; }
  .games .g { display: grid; grid-template-columns: auto auto 1fr; gap: 10px; padding: 5px 2px; border-bottom: 1px dashed ${({ theme }) => theme.colors.border}; font-size: 0.81rem; }
  .games .sc { font-weight: 800; font-variant-numeric: tabular-nums; }
  .games .cp { text-align: right; color: ${({ theme }) => theme.colors.textMuted}; }
`;
